import { useState, useEffect, useCallback } from "react";
import { invoke } from "./lib/bridge";
import { parseInviteUrl, parseCheckoutParam } from "./lib/urlUtils";
import { TabBar, Tab } from "./components/TabBar";
import { TodayTab } from "./components/TodayTab";
import { ListsTab } from "./components/ListsTab";
import { NotesTab } from "./components/NotesTab";
import { FamilyTab } from "./components/FamilyTab";
import { Toast } from "./components/Toast";
import { CreateFamily } from "./components/CreateFamily";
import { JoinFamily, type InviteData } from "./components/JoinFamily";
import { JoinWithCode } from "./components/JoinWithCode";
import { AppShell } from "./components/AppShell";
import { ScreenRouter } from "./components/ScreenRouter";
import { useVisibility } from "./hooks/useVisibility";
import { useForcedLogout } from "./hooks/useForcedLogout";
import { useSyncPoll } from "./hooks/useSyncPoll";
import { useNotificationSocket } from "./hooks/useNotificationSocket";
import { useJoinRequestToasts } from "./hooks/useJoinRequestToasts";
import "./App.css";

interface FamilyInfo {
  familyId: string;
  familyName: string;
  memberName: string;
  memberId: number;
  publicKey: string;
}

export interface DocMeta {
  docId: string;
  docType: string;
  title: string;
  createdAt: number;
}


type Screen =
  | { type: "tabs" }
  | { type: "list"; doc: DocMeta }
  | { type: "list-item"; doc: DocMeta; itemId: string }
  | { type: "note"; doc: DocMeta }
  | { type: "meal"; doc: DocMeta }
  | { type: "member"; name: string; isMe: boolean; avatarUrl: string | null; online: boolean; lastActiveAt: string | null }
  | { type: "invite" }
  | { type: "link-device" }
  | { type: "join-family" }
  | { type: "help" }
  | { type: "account" };

function TabApp({ family, onFamilyChanged, initialCheckout }: { family: FamilyInfo; onFamilyChanged: (info: FamilyInfo | null) => void; initialCheckout?: "success" | "cancel" | null }) {
  const [activeTab, setActiveTab] = useState<Tab>(initialCheckout ? "family" : "today");
  const [screen, setScreen] = useState<Screen>(initialCheckout ? { type: "account" } : { type: "tabs" });
  const goBack = useCallback(() => setScreen({ type: "tabs" }), []);

  // Auth token and API URL for WebSocket connections and authenticated calls
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [apiUrl, setApiUrl] = useState("");
  useEffect(() => {
    invoke<string | null>("get_auth_token").then(setAuthToken).catch(() => {});
    invoke<string>("get_api_url").then(setApiUrl).catch(() => {});
  }, []);

  const isVisible = useVisibility();

  // Sync poll, resync cycle, update check
  const { syncVersion, resyncing, updateAvailable, fulfillPendingResyncsRef, collectResyncRef } = useSyncPoll({ isVisible });

  // Determine which chat channel is currently being viewed
  const activeChannel = screen.type === "tabs" && activeTab === "family"
    ? "family"
    : screen.type === "meal"
      ? screen.doc.docId
      : null;

  // Notification WebSocket, fallback poll, roster, chat prune
  useNotificationSocket({
    family,
    authToken,
    apiUrl,
    isVisible,
    activeChannel,
    fulfillPendingResyncsRef,
    collectResyncRef,
  });

  // Join request toasts
  const { toasts, removeToast } = useJoinRequestToasts({ isVisible });

  // Detail screen (any non-tabs screen)
  if (screen.type !== "tabs") {
    return (
      <ScreenRouter
        screen={screen}
        family={family}
        syncVersion={syncVersion}
        authToken={authToken}
        apiUrl={apiUrl}
        onBack={goBack}
        onSetScreen={setScreen}
        onFamilyChanged={onFamilyChanged}
        initialCheckout={initialCheckout}
      />
    );
  }

  return (
    <AppShell
      familyName={family.familyName}
      banners={
        <>
          {updateAvailable && (
            <div className="update-banner" role="status">
              <span>A new version is available.</span>
              <button type="button" className="update-banner__refresh" onClick={() => window.location.reload()}>
                Refresh
              </button>
            </div>
          )}
          {resyncing && (
            <div className="resync-banner" role="status">
              Catching up... waiting for a family member to come online to sync.
            </div>
          )}
        </>
      }
      overlays={
        <>
          <Toast toasts={toasts} onDismiss={removeToast} />
          <TabBar active={activeTab} onChange={setActiveTab} />
        </>
      }
    >
      {activeTab === "today" && <TodayTab syncVersion={syncVersion} />}
      {activeTab === "lists" && (
        <ListsTab syncVersion={syncVersion} onOpenList={(doc) => setScreen({ type: "list", doc })} />
      )}
      {activeTab === "meals" && (
        <NotesTab key="meals" docType="meal" title="Meals" syncVersion={syncVersion} onOpenNote={(doc) => setScreen({ type: "meal", doc })} />
      )}
      {activeTab === "notes" && (
        <NotesTab key="notes" syncVersion={syncVersion} onOpenNote={(doc) => setScreen({ type: "note", doc })} />
      )}
      {activeTab === "family" && (
        <FamilyTab
          memberName={family.memberName}
          memberId={family.memberId}
          authToken={authToken}
          apiUrl={apiUrl}
          onOpenMember={(name, avatarUrl, online, lastActiveAt) => setScreen({ type: "member", name, isMe: name === family.memberName, avatarUrl, online, lastActiveAt })}
          onInvite={() => setScreen({ type: "invite" })}
          onJoinFamily={() => setScreen({ type: "join-family" })}
          onHelp={() => setScreen({ type: "help" })}
          onAccount={() => setScreen({ type: "account" })}
        />
      )}
    </AppShell>
  );
}

function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);
  return online;
}

function App() {
  const [family, setFamily] = useState<FamilyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingInvite, setPendingInvite] = useState<InviteData | null>(null);
  const [showJoinCode, setShowJoinCode] = useState(false);
  const [checkoutReturn, setCheckoutReturn] = useState<"success" | "cancel" | null>(null);
  const online = useOnlineStatus();

  const handleDeepLink = useCallback((urls: string[]) => {
    for (const url of urls) {
      const invite = parseInviteUrl(url);
      if (invite) {
        setPendingInvite(invite);
        // Clean the invite params out of the address bar so a refresh doesn't re-prompt.
        window.history.replaceState({}, "", "/");
        return;
      }
    }
  }, []);

  useEffect(() => {
    async function init() {
      try {
        const info = await invoke<FamilyInfo | null>("get_family_status");
        setFamily(info);
      } catch (e) {
        console.error("Failed to check family status:", e);
      }
      setLoading(false);

      // Returning from Stripe Checkout (success/cancel). Clear the param so a
      // refresh doesn't re-trigger, and route into the Account screen.
      const checkout = parseCheckoutParam(window.location.search);
      if (checkout) {
        setCheckoutReturn(checkout);
        window.history.replaceState({}, "", "/");
        return;
      }

      // Handle an invite link that arrived as the page URL (PWA replacement for deep links).
      handleDeepLink([window.location.href]);
    }
    init();
  }, [handleDeepLink]);

  // The server rejected this device's auth (401) — it's been removed from the
  // family (e.g. booted on a downgrade to Free). Wipe local state and drop back
  // to onboarding so stale family data (reminders, notes, chat) isn't readable
  // on this device anymore.
  useForcedLogout(() => {
    setPendingInvite(null);
    setShowJoinCode(false);
    setFamily(null);
    window.alert("You're no longer a member of this family, so you've been signed out on this device.");
  });

  if (loading) {
    return (
      <main className="container">
        <p>Loading...</p>
      </main>
    );
  }

  const offlineBanner = !online ? (
    <div className="offline-banner">You're offline — changes will sync when you reconnect</div>
  ) : null;

  // Show join screen if we have a pending invite and no family yet
  if (pendingInvite && !family) {
    return (
      <>
        {offlineBanner}
        <JoinFamily
          invite={pendingInvite}
          onJoined={(info) => {
            setPendingInvite(null);
            setFamily(info);
          }}
          onCancel={() => setPendingInvite(null)}
        />
      </>
    );
  }

  // A device-link URL opened on a device that's already set up — nothing to do.
  if (pendingInvite && pendingInvite.deviceLink && family) {
    return (
      <>
        {offlineBanner}
        <main className="container">
          <h1 className="family-name">{family.familyName}</h1>
          <p style={{ textAlign: "center", color: "var(--color-hint)", marginTop: "24px", marginBottom: "24px" }}>
            This device is already part of <strong>{family.familyName}</strong>.
          </p>
          <button onClick={() => setPendingInvite(null)} className="btn-primary btn-full">
            OK
          </button>
        </main>
      </>
    );
  }

  // Show join confirmation if we have a pending invite but already in a family
  if (pendingInvite && family) {
    return (
      <>
        {offlineBanner}
        <main className="container">
          <h1 className="family-name">Switch Family?</h1>
          <p style={{ textAlign: "center", color: "var(--color-hint)", marginBottom: "24px" }}>
            You're currently in <strong>{family.familyName}</strong>.
            Do you want to leave and join <strong>{pendingInvite.n}</strong>?
          </p>
          <button
            onClick={() => {
              setFamily(null);
            }}
            className="btn-primary btn-full"
          >
            Join {pendingInvite.n}
          </button>
          <button
            onClick={() => setPendingInvite(null)}
            className="btn-secondary btn-full"
            style={{ marginTop: "12px" }}
          >
            Stay in {family.familyName}
          </button>
        </main>
      </>
    );
  }

  if (!family && showJoinCode) {
    return (
      <>
        {offlineBanner}
        <JoinWithCode
          onJoined={(info) => {
            setShowJoinCode(false);
            setFamily(info);
          }}
          onCancel={() => setShowJoinCode(false)}
        />
      </>
    );
  }

  if (!family) {
    return (
      <>
        {offlineBanner}
        <CreateFamily onCreated={setFamily} onJoinWithCode={() => setShowJoinCode(true)} />
      </>
    );
  }

  return (
    <>
      {offlineBanner}
      <TabApp family={family} onFamilyChanged={setFamily} initialCheckout={checkoutReturn} />
    </>
  );
}

export default App;
